import { Link as MuiLink, Card, CardContent, Typography, Theme } from '@mui/material';
import { Link } from 'react-router-dom';
import { UserNotification } from '@models/notification';
import * as styles from '@components/notification/notificationCard/NotificationCard.styles';
import { NotificationCardHeader } from '@components/notification/notificationCard/NotificationCardHeader';
import { getNotificationUrl } from '@components/notification/notificationCard/NotificationCard.icon';
import { adjustFontSize, adjustLineHeight } from '@theme/styleOverrides/typography';
import { DateTimeFormat, getDateFormatter } from '@utils/date';
import { useStore } from '@store/GlobalStore';
import { format } from 'date-fns';
import { shallow } from 'zustand/shallow';

const LINES_TO_SHOW = '3';

export type INotificationCard = {
  onDismiss: () => void;
  onRead: () => void;
  onClosePopover: () => void;
} & UserNotification;

export const NotificationCard = (props: INotificationCard) => {
  const {
    createdAt,
    heading,
    message,
    onClosePopover,
    onDismiss,
    onRead,
    quoteId,
    readAt,
    title,
    billingAccountId,
    paymentRequestId,
    notificationDefinition,
  } = props;
  const { layoutPreferences } = useStore((state) => ({ layoutPreferences: state.layoutPreferences }), shallow);
  const clockFormat = layoutPreferences.clockFormat;
  const policyId = (props as any)?.policyId;

  let url = props.link;
  if (!url && notificationDefinition.notificationType in getNotificationUrl) {
    url = getNotificationUrl[notificationDefinition.notificationType as keyof typeof getNotificationUrl]({
      quoteId: quoteId ?? undefined,
      policyId: policyId ?? undefined,
      billingAccountId: billingAccountId ?? undefined,
      paymentRequestId: paymentRequestId ?? undefined,
    });
  }

  if (!url) {
    // attempt defaults if above mapping is not found
    if (quoteId) {
      url = `/quote/${quoteId}`;
    } else if (paymentRequestId) {
      url = `/payments/payments-detail/${paymentRequestId}`;
    } else if (billingAccountId) {
      url = `/payments/account/${billingAccountId}`;
    } else if (policyId) {
      url = `/policy/${policyId}`;
    }
  }

  const isUnread = !!readAt;
  const bgColorSx = ({ palette }: Theme) =>
    palette.theme === 'lio'
      ? {
          '&.MuiPaper-elevation0': {
            '&.MuiCard-root': {
              backgroundColor: !isUnread ? `#586D84` : palette.background.light,
              '&:hover': {
                backgroundColor: '#627F9F',
              },
            },
          },
        }
      : {
          '&.MuiPaper-elevation0': {
            '&.MuiCard-root': {
              backgroundColor: !isUnread ? palette.background.light : `${palette.background.light}10`,
              '&:hover': {
                // tint over the card's own color: swapping in the translucent tint shows the darker panel and darkens it
                backgroundImage: `linear-gradient(${palette.panel.main}, ${palette.panel.main})`,
              },
            },
          },
        };

  const handleNotificationRead = () => {
    onRead();
    onClosePopover();
  };

  const Notification = (
    <Card elevation={0} sx={[styles.cardSx, bgColorSx]}>
      <NotificationCardHeader
        iconName={notificationDefinition.iconName}
        heading={heading}
        createdAt={format(new Date(createdAt), getDateFormatter(clockFormat, DateTimeFormat))}
        onDismiss={onDismiss}
      />
      <CardContent className='keep-padding'>
        <Typography
          variant='tableHeader'
          sx={{
            maxWidth: '250px',
            paddingLeft: '23px',
            lineHeight: '20px',
            // LIO's light seafoam reads as teal on the other palettes, so they use their accent like other links
            color: ({ palette }: Theme) => (palette.theme === 'lio' ? palette.secondary.dark : palette.secondary.main),
            fontWeight: 'bold',
          }}
        >
          {title}
        </Typography>
        <Typography
          variant='body'
          sx={{
            display: '-webkit-box',
            paddingLeft: '23px',
            fontSize: ({ palette }: Theme) => `${adjustFontSize(14, palette.fontSize)}px`,
            lineHeight: ({ palette }: Theme) => `${adjustLineHeight(20, palette.fontSize)}px`,
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: LINES_TO_SHOW,
            overflow: 'hidden',
          }}
        >
          {message}
        </Typography>
      </CardContent>
    </Card>
  );

  return url ? (
    <MuiLink to={url} underline='none' component={Link} onClick={handleNotificationRead}>
      {Notification}
    </MuiLink>
  ) : (
    Notification
  );
};
